import { Button, JumplinksContainer } from '@/features/shared';
import { MuiChip, MuiGrid, MuiTypography } from '@/features/shared/material-ui';
import { SPA_BASE_URL, Theme } from '@/utils/constants';
import { getSafeURI } from '@/utils/helpers';
import clsx from 'clsx';
import React from 'react';
import { Link } from 'react-router-dom';
import { DetailPageHeader } from '../../shared/DetailPageHeader';
import { TaxonomySummaryBlock } from '../../shared/TaxonomySummaryBlock';
import { SolutionDetailIntroProps } from './IntroContent.interface';
import useStyles from './IntroContent.styles';

const IntroContent = ({
  button,
  displayTags = false,
  headline,
  includeBreadcrumb,
  includeJumplinks,
  labels,
  logo,
  shortDescription,
  summary,
  tags = [],
  theme = Theme.light,
  taxonomyList = {},
  tagLinks,
}: SolutionDetailIntroProps) => {
  const classes = useStyles({});

  const headerProps = {
    headline,
    logo,
    shortDescription,
    theme,
    includeBreadcrumb,
  };

  const summaryBlockProps = {
    labels,
    summary,
    theme,
  };

  const rootClasses = clsx(classes.wrapper);

  const getTagLink = (key: string) => {
    const keyPaths = key.split('/');

    while (keyPaths.length) {
      const match = tagLinks?.[keyPaths.join('/')];
      if (match) return `${SPA_BASE_URL}${match}`;
      keyPaths.shift();
    }

    return getSafeURI(`${SPA_BASE_URL}/search?q=${taxonomyList?.[key] || ''}`);
  };

  return (
    <MuiGrid container className={rootClasses}>
      <MuiGrid container className={clsx(classes.introTop, classes.introContent)}>
        <MuiGrid className={classes.partnerIntro} item xs={12} md={8}>
          <DetailPageHeader {...headerProps} />
          {button && <Button link={button} theme={theme} />}
        </MuiGrid>

        <MuiGrid className={classes.sidebar} item xs={12} md={4}>
          {includeJumplinks && (
            <JumplinksContainer
              className={classes.jumplinksContainer}
              theme={theme}
              title={labels?.jump_to || 'Jump to'}
            />
          )}

          {displayTags && tags?.length > 0 && (
            <div className={classes.tagsContainer}>
              <MuiTypography className={classes.tagsHeading} variant="overline" component="h3">
                {labels?.tags || 'Tags'}
              </MuiTypography>
              {tags
                .filter((tag) => !!taxonomyList[tag])
                .map((tag, _id) => (
                  <MuiChip
                    className={classes.tagChip}
                    label={taxonomyList[tag]}
                    // eslint-disable-next-line react/no-array-index-key
                    key={`tag_${_id}`}
                    size="small"
                    component={Link}
                    to={getTagLink(tag)}
                    clickable
                  />
                ))}
            </div>
          )}
        </MuiGrid>
      </MuiGrid>

      {summary && (
        <MuiGrid item xs={12} className={clsx(classes.introContent, classes.summaryBlock)}>
          <TaxonomySummaryBlock {...summaryBlockProps} />
        </MuiGrid>
      )}
    </MuiGrid>
  );
};

export default IntroContent;
